import React from "react";
import {
  FileText,
  LayoutTemplate,
  Briefcase,
  ShoppingCart,
  ArrowRight,
  Check,
} from "lucide-react";
import { Button } from "../ui/button";
import { WEB_PACKAGES } from "../../mock";
import { selectPackage } from "../../lib/selectPackage";

const ICONS = { FileText, LayoutTemplate, Briefcase, ShoppingCart };

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function WebDesignPackages() {
  return (
    <section
      id="web-design"
      data-testid="web-design-packages-section"
      className="py-24 lg:py-32 bg-white relative overflow-hidden"
    >
      <div className="absolute -top-24 right-1/4 h-72 w-72 rounded-full bg-sky-100 blur-3xl opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="section-eyebrow">Web Design</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
            Web design packages in Hamilton &amp; Motherwell.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Affordable, professional websites for businesses in Hamilton,
            Motherwell and nearby towns &mdash; from a simple 2 page site to a
            full online shop.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WEB_PACKAGES.map((p) => (
            <WebPackageCard key={p.name} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

const WebPackageCard = ({ p }) => {
  const Icon = ICONS[p.icon];
  const id = slug(p.name);
  return (
    <div
      data-testid={`web-package-card-${id}`}
      className={`relative rounded-3xl border p-7 lg:p-8 bg-white card-lift flex flex-col ${
        p.popular
          ? "border-sky-300 ring-2 ring-sky-200/60 shadow-lg shadow-sky-100/50"
          : "border-slate-200"
      }`}
    >
      {p.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-sky-500 text-white text-[11px] font-semibold uppercase tracking-wider shadow-sm">
          Most popular
        </div>
      )}
      <div
        className={`h-11 w-11 rounded-2xl flex items-center justify-center ${
          p.popular ? "bg-sky-500 text-white" : "bg-slate-900 text-white ring-4 ring-sky-50"
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-5 text-xl font-semibold text-slate-900">{p.name}</h3>
      <p className="text-sm text-sky-700 font-medium mt-1">{p.tagline}</p>
      <div className="mt-5 flex items-baseline gap-1.5" data-testid={`web-package-price-${id}`}>
        {p.suffix && (
          <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            {p.suffix}
          </span>
        )}
        <span className="text-4xl font-semibold text-slate-900">{p.price}</span>
      </div>
      <ul className="mt-6 space-y-2 flex-1">
        {p.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
            <Check className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        data-testid={`web-package-cta-${id}`}
        onClick={() =>
          selectPackage(
            p.service,
            `Hi, I'm interested in the ${p.name} web design package (${p.tagline}, ${p.suffix ? p.suffix + " " : ""}${p.price}).`
          )
        }
        className={`mt-6 rounded-full w-full h-11 group ${
          p.popular ? "bg-sky-600 hover:bg-sky-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"
        }`}
      >
        Choose {p.name}
        <ArrowRight className="ml-1.5 h-4 w-4 btn-arrow" />
      </Button>
    </div>
  );
};
